<script setup lang="ts">
import { parse, stringify } from 'qs';
import SidebarBox from './SidebarBox.vue';
import { type TripSortBy } from '@itinerary/trip/TripModuleTypes';
import type { Radio } from '#imports';

const { autoApply } = defineProps<{
    autoApply?: boolean;
}>();
const currentModule = useCurrentItineraryModule();
const { sortBy, defaultSortBy, isInitialSort } = moduleToRefs(
    currentModule.value
);
const gtm = useGtm();
const route = useRoute();
const sortBy_ = ref('');
const isMounted = useMounted();
const list = computed<Radio<string>[]>(() =>
    currentModule.value.sortByList.map(sort => ({
        label: sort.label,
        value: sort.type,
    }))
);
const loading = computed(() =>
    ['none', 'loading'].includes(
        currentModule.value.currentLoadingStatus || 'none'
    )
);
const updateParams = () => {
    const prevParams = parse(route.fullPath.split('#')[0].split('?')[1], {
        allowDots: true,
    });
    prevParams.sortBy =
        sortBy_.value === defaultSortBy.value ? undefined : sortBy_.value;
    const newParams = stringify(prevParams, {
        addQueryPrefix: true,
        arrayFormat: 'brackets',
        allowDots: true,
        skipNulls: true,
    });
    const newPath = `${route.path}${newParams}`;
    if (route.fullPath.split('#')[0] !== newPath) {
        window.history.replaceState(null, '', newPath);
    }
};
const changeSortBy = (value: string | null) => {
    gtm?.trackEvent({
        event: 'srp-sort-change',
        value,
    });
    sortBy_.value = value || '';
    if (autoApply) {
        apply();
    }
};
const apply = () => {
    sortBy.value = sortBy_.value as TripSortBy;
    isInitialSort.value = false;
    updateParams();
};
const refresh = () => {
    sortBy_.value = sortBy.value;
    updateParams();
};
watch(sortBy, val => {
    sortBy_.value = val;
});
onBeforeMount(() => {
    const sortByParam = parse(route.fullPath.split('?')[1], {
        allowDots: true,
    }).sortBy as string;
    if (sortByParam) {
        sortBy.value = sortByParam;
        sortBy_.value = sortByParam;
    } else {
        sortBy_.value = sortBy.value;
    }
});

defineExpose({
    refresh,
    apply,
});
</script>

<template>
    <div class="sort">
        <SidebarBox class="sort-radio" title="مرتب‌سازی بر اساس">
            <div v-if="!loading && isMounted" class="content">
                <mr-radio-button
                    :radios="list"
                    :model-value="sortBy_"
                    name="sort"
                    @update:model-value="changeSortBy($event)"
                />
            </div>
            <mr-bullet-list-loader
                v-else
                class="loader"
                :count="3"
                :item-height="24"
                :item-max-width="120"
                :item-min-width="60"
            />
        </SidebarBox>
    </div>
</template>

<style scoped lang="scss">
.content {
    padding: 16px;
}
.loader {
    margin-right: 12px;
}
:deep(label) {
    font-size: 11px;
    @include font-weight(bold);
    color: usePalette($p-blue, $p-200);
}
</style>
